import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import { FileText, PlusCircle, Mail, Eye, Clock } from "lucide-react";

export default function AdminDashboard() {
  const [stats, setStats] = useState({ total: 0, published: 0, drafts: 0, contacts: 0 });
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [all, pub, drafts, contacts] = await Promise.all([
          api.get("/admin/articles?limit=5"),
          api.get("/admin/articles?status=published&limit=500"),
          api.get("/admin/articles?status=draft&limit=500"),
          api.get("/contacts?limit=500").catch(() => ({ data: [] })),
        ]);
        setRecent(all.data);
        setStats({
          total: pub.data.length + drafts.data.length,
          published: pub.data.length,
          drafts: drafts.data.length,
          contacts: contacts.data.length,
        });
      } catch (e) {
        // handled by interceptor
      }
    })();
  }, []);

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-slate-900">Dashboard</h1>
        <p className="text-slate-600 mt-1">Resumen de tu sitio</p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        <StatCard icon={FileText} label="Artículos totales" value={stats.total} color="from-cyan-500 to-teal-500" />
        <StatCard icon={Eye} label="Publicados" value={stats.published} color="from-emerald-500 to-green-600" />
        <StatCard icon={Clock} label="Borradores" value={stats.drafts} color="from-amber-500 to-orange-500" />
        <StatCard icon={Mail} label="Mensajes recibidos" value={stats.contacts} color="from-indigo-500 to-purple-600" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-slate-900">Artículos recientes</h2>
            <Link to="/admin/articulos" className="text-sm text-cyan-700 hover:text-cyan-900 font-medium">Ver todos →</Link>
          </div>
          {recent.length === 0 ? (
            <p className="text-slate-500 text-sm py-6 text-center">No hay artículos aún. <Link to="/admin/articulos/nuevo" className="text-cyan-700 font-medium">Crear el primero</Link></p>
          ) : (
            <div className="divide-y divide-slate-100">
              {recent.map((a) => (
                <Link key={a.id} to={`/admin/articulos/${a.id}/editar`} className="flex items-center gap-4 py-3 hover:bg-slate-50 -mx-2 px-2 rounded-lg transition">
                  {a.featured_image && (
                    <img src={a.featured_image} alt="" className="w-14 h-14 rounded-lg object-cover flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-slate-900 truncate">{a.title}</div>
                    <div className="flex items-center gap-2 text-xs text-slate-500 mt-0.5">
                      <span className="bg-slate-100 px-2 py-0.5 rounded">{a.category}</span>
                      <StatusBadge status={a.status} small />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

        <div className="bg-gradient-to-br from-cyan-600 to-teal-700 rounded-2xl p-6 text-white shadow-lg">
          <h2 className="text-xl font-bold mb-2">Accesos rápidos</h2>
          <p className="text-cyan-100 text-sm mb-5">Gestión habitual del blog</p>
          <div className="space-y-2">
            <Link to="/admin/articulos/nuevo" className="flex items-center gap-3 bg-white/10 hover:bg-white/20 rounded-xl px-4 py-3 transition">
              <PlusCircle size={18} />
              <span className="font-medium">Nuevo artículo</span>
            </Link>
            <Link to="/admin/articulos" className="flex items-center gap-3 bg-white/10 hover:bg-white/20 rounded-xl px-4 py-3 transition">
              <FileText size={18} />
              <span className="font-medium">Gestionar artículos</span>
            </Link>
            <Link to="/admin/mensajes" className="flex items-center gap-3 bg-white/10 hover:bg-white/20 rounded-xl px-4 py-3 transition">
              <Mail size={18} />
              <span className="font-medium">Ver mensajes</span>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
      <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${color} flex items-center justify-center text-white mb-3`}>
        <Icon size={20} />
      </div>
      <div className="text-3xl font-bold text-slate-900">{value}</div>
      <div className="text-sm text-slate-500 mt-0.5">{label}</div>
    </div>
  );
}

function StatusBadge({ status, small }) {
  const map = {
    published: { txt: "Publicado", cls: "bg-emerald-100 text-emerald-700" },
    draft: { txt: "Borrador", cls: "bg-amber-100 text-amber-700" },
  };
  const it = map[status] || { txt: status, cls: "bg-slate-100 text-slate-700" };
  return <span className={`${it.cls} ${small ? "text-xs px-2 py-0.5" : "text-sm px-2.5 py-1"} rounded-full font-medium`}>{it.txt}</span>;
}
